import React, { useEffect, useState } from "react";
import * as PropTypes from "prop-types";
import { Input, Typography, Switch } from "antd";
import { Select } from "../components";
import { useIntl } from "react-intl";
import { fetchApiData, listApiData } from "../../../api/server";
import { useDispatch } from "react-redux";
import { updateDynamicFilters } from "../../report/reportSlice";
import {
  getFullName,
  getAnalyticsValue,
} from "../../../components/util/helpers";
import { useSelector } from "react-redux";
import { useQuery } from "react-query";
import moment from "moment";
import { DatePicker } from "../../../components/antOverrides";
const { RangePicker } = DatePicker;

const { Text } = Typography;
const professionalFilter = {
  $and: [
    {
      $or: [
        { professional_role_id: 1 },
        { professional_role_id: 2 },
        { professional_role_id: 3 },
        { professional_role_id: 4 },
      ],
    },
  ],
};

const staffFilter = { $and: [{ staff_member: true }] };

const mapping = {
  branches: {
    path: "branches",
    displayName: (rec) => rec.name,
    fieldValue: (rec) => rec.id.toString(),
    page: {
      pageSize: 100,
      order: {
        field: "name",
        order: "ascend",
      },
    },
  },
  clients: {
    path: "clients",
    displayName: (rec) => rec.name,
    fieldValue: (rec) => rec.id.toString(),
    page: {
      pageSize: 100,
      order: {
        field: "name",
        order: "ascend",
      },
    },
  },
  employees: {
    path: "employees",
    displayName: (rec) => getFullName(rec.surename, rec.first_name),
    fieldValue: (rec) => rec.id.toString(),
    page: {
      pageSize: 100,
      order: {
        field: "surename",
        order: "ascend",
      },
    },
  },
  professionalEmployees: {
    path: "employees",
    displayName: (rec) => getFullName(rec.surename, rec.first_name),
    fieldValue: (rec) => rec.id.toString(),
    page: {
      pageSize: 100,
      order: {
        field: "surename",
        order: "ascend",
      },
      filter: professionalFilter,
    },
  },
  patients: {
    path: "patients",
    displayName: (rec) => getFullName(rec.surename, rec.first_name),
    fieldValue: (rec) => rec.id.toString(),
    page: {
      pageSize: 100,
      order: {
        field: "surename",
        order: "ascend",
      },
    },
  },
  staffEmployees: {
    path: "employees",
    displayName: (rec) => getFullName(rec.surename, rec.first_name),
    fieldValue: (rec) => rec.id.toString(),
    page: {
      pageSize: 100,
      order: {
        field: "surename",
        order: "ascend",
      },
      filter: staffFilter,
    },
  },
};

const { Option } = Select;
const FilterInputs = {
  entity: ({ key, values, onChange, list, placeholder, onSearch, onBlur }) => {
    return (
      <Select
        key={key}
        style={{ width: 300 }}
        mode="tags"
        onChange={onChange}
        value={values}
        placeholder={placeholder}
        onSearch={onSearch}
        onBlur={onBlur}
        showSearch={true}
        options={list}
        filterOption={false}
      />
    );
  },
  string: ({
    key,
    values,
    onChange,
    list,
    intl,
    placeholder,
    onSearch,
    onBlur,
  }) => {
    return (
      <Select
        key={key}
        style={{ width: 300 }}
        mode="tags"
        onChange={onChange}
        value={values}
        placeholder={placeholder}
        onSearch={onSearch}
        onBlur={onBlur}
        showSearch={true}
        filterOption={true}
      >
        {list.map((option) => (
          <Option key={option.name} value={option.value}>
            {getAnalyticsValue(intl, option.name)}
          </Option>
        ))}
      </Select>
    );
  },
  number: ({ key, values, onChange, placeholder }) => (
    <Input
      key={key}
      style={{ width: 300 }}
      onChange={(e) => onChange([e.target.value])}
      value={(values && values[0]) || ""}
      placeholder={placeholder}
    />
  ),
  checkbox: ({ key, onChange }) => {
    return (
      <div
        style={{
          display: "flex",
          height: "34px",
          justifyContent: "start",
          alignItems: "center",
        }}
      >
        <Switch key={key} onChange={onChange} />
      </div>
    );
  },
  boolean: ({ key, values, onChange, list, intl, placeholder }) => (
    <Select
      key={key}
      style={{ width: 200 }}
      mode="tags"
      onChange={onChange}
      value={values}
      placeholder={placeholder}
    >
      {[
        {
          name: intl.formatMessage({
            id: `app.analytics.values.true`,
          }),
          value: "true",
        },
        {
          name: intl.formatMessage({
            id: `app.analytics.values.false`,
          }),
          value: "false",
        },
      ].map((option) => (
        <Option key={option.name} value={option.value}>
          {getAnalyticsValue(intl, option.name)}
        </Option>
      ))}
    </Select>
  ),
  daterange: ({ key, values, onChange, placeholder }) => (
    <RangePicker
      format="DD-MM-YYYY"
      inputReadOnly={true}
      defaultValue={[].map((date) => moment(date))}
      onChange={(dateRange) =>
        onChange(
          dateRange
            ? dateRange.map((dateTime) => dateTime.format("YYYY-MM-DD"))
            : []
        )
      }
    />
  ),
  date: ({ key, values, onChange, placeholder }) => (
    <DatePicker
      format="DD-MM-YYYY"
      inputReadOnly={true}
      defaultValue={null}
      onChange={(date) =>
        onChange(date ? [moment(date).format("YYYY-MM-DD")] : [])
      }
    />
  ),
};

FilterInputs.string.propTypes = {
  values: PropTypes.array,
  onChange: PropTypes.func.isRequired,
};

FilterInputs.string.defaultProps = {
  values: [],
};

FilterInputs.number.propTypes = {
  values: PropTypes.array,
  onChange: PropTypes.func.isRequired,
};

FilterInputs.number.defaultProps = {
  values: [],
};

const FilterInput = ({ member, updateMethods, type }) => {
  const [selectData, setSelectData] = useState([]);
  const [value, setValue] = useState();
  const scopePrefix = useSelector((state) => state.user.scopePrefix);
  const dispatch = useDispatch();
  const { isLoading, data } = useQuery(
    [member?.dimension?.meta, scopePrefix, value],
    async () => {
      const result =
        member?.dimension?.meta?.type === "fetched_list"
          ? await fetchApiData(member.dimension.meta.path)
          : member?.dimension?.meta?.type === "entity"
          ? await listApiData({
              path: mapping[member.dimension.meta.name].path,
              scopePrefix,
              ...mapping[member.dimension.meta.name].page,
              filter: {
                ...mapping[member.dimension.meta.name].page.filter,
                freeText: value,
              },
            })
          : null;
      return result?.data?.items ? result.data.items : [];
    },
    {
      keepPreviousData: false,
      staleTime: 0,
      enabled: member !== null,
    }
  );

  useEffect(() => {
    const final =
      member?.dimension?.meta?.type === "predefined_list"
        ? member.dimension.meta.list.map((i) => {
            return {
              name: getAnalyticsValue(intl, i.name),
              value: i.name,
            };
          })
        : member?.dimension?.meta?.type === "fetched_list" &&
          data &&
          data.length > 0
        ? data.map((i) => {
            return {
              name: i[member.dimension.meta.key],
              value: i[member.dimension.meta.key],
            };
          })
        : member?.dimension?.meta?.type === "entity" && data && data.length > 0
        ? data.map((i) => {
            return {
              label: mapping[member.dimension.meta.name].displayName(i),
              value: mapping[member.dimension.meta.name].fieldValue(i),
            };
          })
        : [];
    setSelectData(final);
  }, [data, member]);

  const Filter =
    member.operator === "set" || member.operator === "notSet"
      ? FilterInputs.checkbox
      : member?.dimension?.type === "boolean"
      ? FilterInputs.boolean
      : member.operator === "inDateRange" ||
        member.operator === "notInDateRange"
      ? FilterInputs.daterange
      : member.operator === "afterDate" || member.operator === "beforeDate"
      ? FilterInputs.date
      : (member?.dimension?.meta?.type &&
          FilterInputs[member.dimension.meta.type]) ||
        FilterInputs.string;

  const intl = useIntl();

  const onChange = (val) => {
    const itemId = `${val.member}${val.operator}`;
    const updated =
      val.operator === "set" || member.operator === "notSet"
        ? {
            ...val,
            values: val.values === true ? [`true`] : [],
            id: itemId,
          }
        : { ...val, id: itemId };
    dispatch(updateDynamicFilters(updated));
  };

  return type === "dynamic" ? (
    <div style={{ display: "block", float: "right", marginLeft: "10px" }}>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <Text type="secondary">{member.name}</Text>
        <Filter
          key={member.id}
          values={member.values}
          onChange={(values) => onChange({ ...member, values })}
          list={selectData}
          intl={intl}
          placeholder={`${intl.formatMessage({
            id: `app.analytics.${member.member}`,
          })} (${intl.formatMessage({
            id: `app.reports.${member.operator}`,
          })})`}
          onSearch={(newValue) => {
            setValue(newValue);
          }}
          onBlur={() => {
            setValue("");
          }}
        />
      </div>
    </div>
  ) : member.operator === "set" || member.operator === "notSet" ? null : (
    <Filter
      key={member.id}
      values={member.values}
      onChange={(values) =>
        updateMethods.update(member, {
          ...member,
          values:
            member.operator === "set" || member.operator === "notSet"
              ? [`true`]
              : values,
        })
      }
      list={selectData}
      intl={intl}
      onSearch={(newValue) => {
        setValue(newValue);
      }}
      onBlur={() => {
        setValue("");
      }}
    />
  );
};

FilterInput.propTypes = {
  member: PropTypes.object.isRequired,
  updateMethods: PropTypes.object.isRequired,
};

export default FilterInput;
